/**
 * ConflagrationSkillEffect — Fire tree vas_flam path: evolution replacing Vas Flam.
 *
 * The cone widens to 70°. On activation, each burning enemy in the cone has
 * their DoT consumed: remaining ticks are converted to instant burst damage
 * (1.5× the total DoT value remaining), burning is removed, and fresh burning
 * is reapplied. Non-burning targets receive only fresh burning and direct damage.
 *
 * Inherits Extended Reach geometry from the 'vas_flam' resolver scope:
 * `rangeTiles` and `spreadDegrees`. The base 70° spread is the minimum;
 * inherited bonuses are additive from the 45° Vas Flam base.
 *
 * Incandescent (`vas_flam_path_passive`) activates a pre-cast multiplier:
 * each enemy already burning from a Vas Flam-path skill (sourceSkillId in
 * ['vas_flam', 'conflagration']) contributes +8% to all cone damage, capped
 * at +40% at 5 targets. Checked by `resolver.hasEffect('conflagration',
 * 'burnStackBonusDamage')`.
 *
 * Fizzle consumes cooldown. Empty cone (no targets) does not consume cooldown.
 * Breaks Hide on activation.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see fire-spec.md § Conflagration, § Incandescent
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Skill IDs whose burning applications count for Incandescent's bonus. */
const VAS_FLAM_PATH_SKILLS: readonly string[] = ['vas_flam', 'conflagration'];

/** Default tick interval for `burning` in ms (from status-effects.json). */
const BURNING_DEFAULT_TICK_MS = 500;

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/** Data about a single active burning instance on a target. */
export interface TargetBurningInstance {
  readonly stacks: number;
  readonly intensity: number;
  readonly remainingMs: number;
  /** If set, overrides the default 500ms tick interval for burst calculation. */
  readonly resolvedTickIntervalMs?: number;
  /** Source skill that applied this burning (used by Incandescent filter). */
  readonly sourceSkillId: string;
}

export interface ConflagrationDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /**
   * Returns all active burning instances for a target.
   * Returns [] if the target has no burning.
   */
  readonly getTargetBurningInstances: (targetId: string) => TargetBurningInstance[];
  /** Remove all burning from a target (DoT consumption). */
  readonly clearTargetBurning: (targetId: string) => void;
  /** Apply fresh burning to a target after consumption or for new targets. */
  readonly applyBurning: (targetId: string, config: {
    sourceSkillId: string;
    durationMs: number;
    intensity: number;
    stacks: number;
    thresholdMs?: number;
  }) => void;
  /**
   * Returns entity IDs of enemies within the cone. Centered on the player
   * position, extending in the facing direction.
   */
  readonly getConeTargets: (lengthTiles: number, angleRangeDeg: number, facingAngle: number) => string[];
  /** Player's current facing angle in radians. */
  readonly getPlayerFacing: () => number;
  /** Returns true if activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /**
   * Deal direct or burst damage to a target.
   * Called with burst damage for burning targets, direct hit damage otherwise.
   */
  readonly dealDamage: (targetId: string, damage: number) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ConflagrationConfig {
  /** Cooldown in ms. Default: 1000 (medium tier). */
  readonly cooldownMs: number;
  /**
   * Base direct hit damage for non-burning targets.
   * Burning targets receive burst damage instead.
   */
  readonly baseDirectDamage: number;
  /** Base burning intensity (damage per tick × stacks = tick damage). */
  readonly baseDotIntensity: number;
  /** Base burning duration in ms. */
  readonly baseDotDurationMs: number;
  /** Cone range in tiles. Extended Reach adds +1. */
  readonly coneLength: number;
  /**
   * Base cone spread in degrees. This is Conflagration's own base (70°),
   * which supersedes Vas Flam's 45°. Extended Reach's +10° applies from the
   * 45° Vas Flam base, so the effective angle is max(70, 45 + bonusAngle).
   */
  readonly coneAngle: number;
  /** Burning stacks applied on fresh application. */
  readonly stacksPerHit: number;
  /**
   * Burst damage multiplier applied to consumed DoT remaining value.
   * spec: 1.5×.
   */
  readonly burstMultiplier: number;
}

const DEFAULT_CONFIG: ConflagrationConfig = {
  cooldownMs: 1000,           // medium cooldown tier
  baseDirectDamage: 8,        // same as Vas Flam direct hit
  baseDotIntensity: 2,        // default burning intensity (from status-effects.json)
  baseDotDurationMs: 3000,    // base 3s
  coneLength: 3,              // base range (Extended Reach adds +1 via resolver)
  coneAngle: 70,              // Conflagration's base spread (wider than Vas Flam 45°)
  stacksPerHit: 1,
  burstMultiplier: 1.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ConflagrationSkillEffect implements SkillEffect {
  readonly skillId = 'conflagration';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: ConflagrationDeps;
  private readonly config: ConflagrationConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _lastHitCount = 0;
  private _lastBurstDamage = 0;

  constructor(deps: ConflagrationDeps, config: Partial<ConflagrationConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get lastHitCount(): number { return this._lastHitCount; }
  /** Total burst damage dealt in the last successful activation. */
  get lastBurstDamage(): number { return this._lastBurstDamage; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle
    if (!this.deps.rollSkillSuccess('conflagration')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    this.deps.breakHide();

    // Resolve geometry from vas_flam resolver scope (Extended Reach inheritance)
    const bonusRange = this.deps.resolver.getAdditiveEffect('vas_flam', 'rangeTiles');
    const bonusAngle = this.deps.resolver.getAdditiveEffect('vas_flam', 'spreadDegrees');

    const resolvedRange = this.config.coneLength + bonusRange;
    // 70° is Conflagration's base; Extended Reach adds from Vas Flam's 45° base.
    // Effective: max(70°, 45° + bonus). Ensures Conflagration is never narrower
    // than Vas Flam + upgrade even though Conflagration's own base is wider.
    const resolvedAngle = Math.max(this.config.coneAngle, 45 + bonusAngle);

    const facingAngle = this.deps.getPlayerFacing();
    const targets = this.deps.getConeTargets(resolvedRange, resolvedAngle, facingAngle);
    this._lastHitCount = targets.length;

    // Empty cone — fizzle; no cooldown, no stamina
    if (targets.length === 0) {
      return false;
    }

    // Incandescent bonus: pre-cast multiplier from pre-existing Vas Flam-path burns
    let incandMultiplier = 1.0;
    if (this.deps.resolver.hasEffect('conflagration', 'burnStackBonusDamage')) {
      const bonusPerTarget = this.deps.resolver.getAdditiveEffect('conflagration', 'burnStackBonusDamage');
      const cap = this.deps.resolver.getAdditiveEffect('conflagration', 'cap');
      let vasFlamBurningCount = 0;
      for (const targetId of targets) {
        const instances = this.deps.getTargetBurningInstances(targetId);
        if (instances.some(inst => VAS_FLAM_PATH_SKILLS.includes(inst.sourceSkillId))) {
          vasFlamBurningCount++;
        }
      }
      incandMultiplier = 1.0 + Math.min(vasFlamBurningCount * bonusPerTarget, cap);
    }

    // Process each target in cone
    this._lastBurstDamage = 0;
    for (const targetId of targets) {
      const burningInstances = this.deps.getTargetBurningInstances(targetId);

      if (burningInstances.length > 0) {
        // DoT consumption: sum remaining value across all burning instances
        let rawBurst = 0;
        for (const inst of burningInstances) {
          const tickInterval = inst.resolvedTickIntervalMs ?? BURNING_DEFAULT_TICK_MS;
          const ticksRemaining = Math.floor(inst.remainingMs / tickInterval);
          rawBurst += inst.intensity * inst.stacks * ticksRemaining * this.config.burstMultiplier;
        }
        const burst = rawBurst * incandMultiplier;
        this._lastBurstDamage += burst;
        this.deps.dealDamage(targetId, burst);
        this.deps.clearTargetBurning(targetId);
      } else {
        // No burning — deal normal direct hit damage
        this.deps.dealDamage(targetId, this.config.baseDirectDamage * incandMultiplier);
      }

      // Apply fresh burning to all targets (burning or not)
      this.deps.applyBurning(targetId, {
        sourceSkillId: 'conflagration',
        durationMs: this.config.baseDotDurationMs,
        intensity: this.config.baseDotIntensity,
        stacks: this.config.stacksPerHit,
      });
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
